import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { mockData } from '../mock';
import { Scale, ArrowRight, CheckCircle2, Clock, Award, BookOpen, Users, Linkedin } from 'lucide-react';

const Trainings = () => {
  const iconMap = {
    'user-injured': '🏥',
    'hard-hat': '⚙️',
    'users': '👨‍👩‍👧‍👦',
    'lightbulb': '💡'
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img src={mockData.logo} alt="JurisGo Logo" className="h-8 w-8 object-contain" />
              <span className="text-2xl font-semibold text-slate-900">JurisGo</span>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Home</Link>
              <Link to="/trainings" className="text-slate-900 font-semibold border-b-2 border-slate-900">Trainings</Link>
              <Link to="/opportunities" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Opportunities</Link>
              <Link to="/blog" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Blog</Link>
              <Link to="/corporate" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Corporate</Link>
              <Link to="/wall-of-fame" className="text-slate-700 hover:text-slate-900 transition-colors font-medium">Wall of Fame</Link>
            </nav>
            <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white transition-colors">
              <a href="/#enroll">Enroll Now</a>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-slate-700 text-white px-4 py-1">Specialized Training Programs</Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Choose Your Specialization
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Select from our comprehensive paralegal training programs designed for specific practice areas. Each program provides practical, workflow-based training with real-world applications.
          </p>
        </div>
      </section>

      {/* Training Programs Grid */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {mockData.trainingPrograms.map((program) => (
              <Card key={program.id} className="border-slate-200 hover:shadow-xl transition-shadow overflow-hidden">
                <CardHeader className="bg-slate-50 border-b border-slate-200 pb-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-4xl">{iconMap[program.icon]}</div>
                    <Badge variant="secondary" className="bg-slate-200 text-slate-800">
                      {program.level}
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl text-slate-900 mb-2">{program.title}</CardTitle>
                  <p className="text-slate-600">{program.description}</p>
                  <div className="flex items-center space-x-4 mt-4 text-sm text-slate-500">
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4" />
                      <span>{program.duration}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Award className="h-4 w-4" />
                      <span>Certificate</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {/* Instructor Section - Only for Personal Injury */}
                  {program.instructor && (
                    <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                        <Award className="h-5 w-5 mr-2 text-slate-700" />
                        {program.instructor.title}
                      </h4>
                      <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-shrink-0">
                          <img 
                            src={program.instructor.image} 
                            alt={program.instructor.name}
                            className="w-32 h-32 rounded-lg object-cover border-2 border-slate-200"
                          />
                          <a 
                            href={program.instructor.linkedin} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center justify-center mt-2 text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            <Linkedin className="h-5 w-5" />
                          </a>
                        </div>
                        <div className="flex-1">
                          <h5 className="text-lg font-semibold text-slate-900 mb-1">{program.instructor.name}</h5>
                          <p className="text-sm font-medium text-slate-700 mb-2">{program.instructor.subtitle}</p>
                          <p className="text-sm text-slate-600 leading-relaxed">{program.instructor.bio}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                      <BookOpen className="h-5 w-5 mr-2 text-slate-700" />
                      What You'll Learn
                    </h4>
                    <ul className="space-y-2">
                      {program.highlights.map((highlight, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-slate-600">
                          <CheckCircle2 className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-slate-900 mb-3">Program Outcomes</h4>
                    <ul className="space-y-2">
                      {program.outcomes.map((outcome, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-slate-600">
                          <ArrowRight className="h-5 w-5 text-slate-700 flex-shrink-0 mt-0.5" />
                          <span>{outcome}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mb-6 bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center">
                      <Award className="h-5 w-5 mr-2 text-slate-700" />
                      Included in This Program
                    </h4>
                    <ul className="space-y-2">
                      <li className="flex items-start space-x-2 text-slate-600">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="font-medium">Capstone Simulation Project (Client Intake till Final Settlement)</span>
                      </li>
                      <li className="flex items-start space-x-2 text-slate-600">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="font-medium">Hands-on Portfolio Practical Development</span>
                      </li>
                      <li className="flex items-start space-x-2 text-slate-600">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="font-medium">Placement Assistance</span>
                      </li>
                      <li className="flex items-start space-x-2 text-slate-600">
                        <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="font-medium">Certificate from a Practicing US Attorney or Paralegal in collaboration with JurisGo</span>
                      </li>
                    </ul>
                  </div>

                  <Button 
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white" 
                    size="lg"
                    asChild={program.id === 'personal-injury'}
                  >
                    {program.id === 'personal-injury' ? (
                      <a href={mockData.razorpayLink} target="_blank" rel="noopener noreferrer">
                        Enroll in {program.title.split(' ')[0]} {program.title.split(' ')[1]}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </a>
                    ) : (
                      <>
                        Enroll in {program.title.split(' ')[0]} {program.title.split(' ')[1]}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Common Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Common to All Programs</h2>
            <p className="text-xl text-slate-600">Every training includes these benefits</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.deliverables.slice(0, 7).map((deliverable, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <Award className="h-8 w-8 text-slate-700 mb-3" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{deliverable.title}</h3>
                <p className="text-sm text-slate-600">{deliverable.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Begin Your Specialization?</h2>
          <p className="text-xl text-slate-300 mb-8">
            Choose your practice area and start your journey to becoming a certified international paralegal
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="bg-white text-slate-900 hover:bg-slate-100 text-lg px-8 py-6">
              <a href="/#enroll">Enroll Now <ArrowRight className="ml-2 h-5 w-5" /></a>
            </Button>
            <Button size="lg" variant="outline" asChild className="border-slate-400 text-white hover:bg-slate-800 text-lg px-8 py-6">
              <a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer">
                Join Community <Users className="ml-2 h-5 w-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={mockData.logo} alt="JurisGo Logo" className="h-6 w-6 object-contain" />
                <span className="text-xl font-semibold text-white">JurisGo</span>
              </div>
              <p className="text-slate-400">India's Premier International Paralegal Training Platform</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Contact</h3>
              <p className="mb-2">Email: {mockData.footer.email}</p>
              <p className="mb-2">Phone: {mockData.footer.phone}</p>
              <p>Location: {mockData.footer.address}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/trainings" className="hover:text-white transition-colors">Trainings</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/corporate" className="hover:text-white transition-colors">Corporate</Link></li>
                <li><a href="/#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><Link to="/admin" className="hover:text-white transition-colors">Admin</Link></li>
                <li><a href={mockData.whatsappGroup} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors flex items-center">
                  <Users className="h-4 w-4 mr-1" /> Join Community
                </a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-700 pt-8">
            <p className="text-sm text-slate-400 mb-4">{mockData.footer.disclaimer}</p>
            <p className="text-center text-slate-500">{mockData.footer.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Trainings;
